import { keepPreviousData, useQueries } from '@tanstack/react-query';
import type { FilterNode } from '@aurora/contracts';
import { post } from '../../lib/api';
import { ticketKeys } from './list-cache';
import {
  combineFilters,
  OPEN_CATEGORIES,
  quickFilterNodes,
  type QuickDimension,
  type QuickFilter,
} from './list-filters';

/**
 * The numbers beside the quick filters.
 *
 * Each chip's count is how many tickets it would match among what the list
 * shows without it: the view or builder's conditions, the search, and the
 * chips switched on in *other* rows. Chips in one row widen each other (OR),
 * so their own row is left out — "Unassigned 4" still reads 4 while "Mine" is
 * on. That is the usual meaning of a count beside a filter, and it is what the
 * list will show if the chip is the only one switched on in its row.
 *
 * One `POST /tickets/facets` per row, grouped by the field the row is about,
 * through the same filter compiler and permission as the list, so a count and
 * the list it predicts cannot disagree. Counted by id where it matters: two
 * agents can share a name, and "Mine" means this account.
 */

export type CountFacet = 'assignee_id' | 'group_id' | 'status_category' | 'sla_state' | 'priority';

interface CountRule {
  facet: CountFacet;
  /** Whether a facet bucket belongs to this chip. */
  matches: (value: string | null, viewer: Viewer) => boolean;
  /** Added to the filter when the facet cannot express the chip, every bucket then counting. */
  own?: boolean;
}

interface Viewer {
  me: number | null;
  groups: ReadonlySet<string>;
}

/** How each chip is counted. A chip with no rule simply shows no number. */
const COUNT_RULES: Record<string, CountRule> = {
  mine: { facet: 'assignee_id', matches: (value, viewer) => viewer.me !== null && value === String(viewer.me) },
  unassigned: { facet: 'assignee_id', matches: (value) => value === null },
  my_groups: { facet: 'group_id', matches: (value, viewer) => value !== null && viewer.groups.has(value) },
  open: { facet: 'status_category', matches: (value) => value === 'open' || value === 'pending' },
  breached: { facet: 'sla_state', matches: (value) => value === 'breached' },
  at_risk: { facet: 'sla_state', matches: (value) => value === 'at_risk' },
  urgent: { facet: 'priority', matches: (value) => value === 'high' || value === 'urgent' },
  // No facet says "created today", so its own condition goes in the filter.
  today: { facet: 'status_category', matches: () => true, own: true },
};

export interface CountRequest {
  /** Stable within a list, for the cache key. */
  id: string;
  facet: CountFacet;
  filter: FilterNode | null;
  /** The chips this request's buckets answer. */
  chips: string[];
}

/**
 * The facet requests that count every chip, given what the list shows before
 * any chip (`base`) and which chips are on.
 */
export function quickCountRequests(options: {
  base: FilterNode | null;
  quick: ReadonlySet<string>;
  chips: QuickFilter[];
}): CountRequest[] {
  const { base, quick, chips } = options;
  const dimensions = [...new Set(chips.map((chip) => chip.dimension))];
  const requests: CountRequest[] = [];

  for (const dimension of dimensions) {
    const inRow = chips.filter((chip) => chip.dimension === dimension);
    const others = new Set([...quick].filter((id) => !inRow.some((chip) => chip.id === id)));
    const context = [base, ...quickFilterNodes(others, chips)];
    // The SLA chips mean open tickets; the status row says so itself when on.
    const openOnly = dimension === 'sla' && !rowIsOn('status', quick, chips);

    const groups = new Map<string, string[]>();
    for (const chip of inRow) {
      const rule = COUNT_RULES[chip.id];
      if (!rule) continue;
      const key = rule.own ? `${rule.facet}:${chip.id}` : rule.facet;
      groups.set(key, [...(groups.get(key) ?? []), chip.id]);
    }

    for (const [key, ids] of groups) {
      const rule = COUNT_RULES[ids[0]!]!;
      const own = rule.own ? chips.find((chip) => chip.id === ids[0])?.condition : undefined;
      requests.push({
        id: `${dimension}:${key}`,
        facet: rule.facet,
        filter: combineFilters([...context, openOnly ? OPEN_CATEGORIES : null, own ?? null]),
        chips: ids,
      });
    }
  }
  return requests;
}

function rowIsOn(dimension: QuickDimension, quick: ReadonlySet<string>, chips: QuickFilter[]): boolean {
  return chips.some((chip) => chip.dimension === dimension && quick.has(chip.id));
}

export type FacetBuckets = Array<{ value: string | null; count: number }>;

/** Each chip's count from the answered requests. A request that failed counts nothing. */
export function countsFrom(
  requests: CountRequest[],
  answers: Array<FacetBuckets | undefined>,
  viewer: { me: number | null; groupIds: number[] },
): Map<string, number> {
  const who: Viewer = { me: viewer.me, groups: new Set(viewer.groupIds.map(String)) };
  const counts = new Map<string, number>();
  requests.forEach((request, index) => {
    const buckets = answers[index];
    if (!Array.isArray(buckets)) return;
    for (const id of request.chips) {
      const rule = COUNT_RULES[id];
      if (!rule) continue;
      counts.set(
        id,
        buckets.reduce(
          (sum, bucket) => (rule.matches(bucket.value ?? null, who) ? sum + (Number(bucket.count) || 0) : sum),
          0,
        ),
      );
    }
  });
  return counts;
}

/**
 * The counts for a list, kept as fresh as the list itself. Until a row's
 * request has answered — or when it fails — its chips show no number: a count
 * that could not be made is not a count of nothing.
 */
export function useQuickCounts(options: {
  base: FilterNode | null;
  quick: ReadonlySet<string>;
  chips: QuickFilter[];
  q: string;
  viewer: { me: number | null; groupIds: number[] };
  enabled: boolean;
}): Map<string, number> {
  const requests = options.enabled ? quickCountRequests(options) : [];
  const results = useQueries({
    queries: requests.map((request) => ({
      queryKey: ticketKeys.count(`quick-${request.id}`, { facet: request.facet, filter: request.filter, q: options.q }),
      queryFn: () =>
        post<{ data: FacetBuckets }>('/tickets/facets', {
          facet: request.facet,
          ...(request.filter ? { filter: request.filter } : {}),
          ...(options.q ? { q: options.q } : {}),
        }),
      placeholderData: keepPreviousData,
      refetchInterval: 60_000,
      staleTime: 30_000,
    })),
  });
  return countsFrom(
    requests,
    results.map((result) => (result.isError ? undefined : result.data?.data)),
    options.viewer,
  );
}
